import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CACHE_TIME, STALE_TIME } from "../ApiProvider";
import { api } from "./api";
import { USE_RETRIEVE_TEAM_FEED } from "./queryKeys";
const USE_RETRIEVE_INTRA_SQUAD_TOURNAMENT = "useRetrieveIntraSquadTournament";
const USE_LIST_INTRA_SQUAD_TOURNAMENT_MATCHES = `useListIntraSquadTournamentMatches`;
const USE_LIST_INTRA_SQUAD_TOURNAMENT_TEAMS = `useListIntraSquadTournamentTeams`;
const USE_RETRIEVE_INTRA_SQUAD_TOURNAMENT_MATCH = `useRetrieveIntraSquadTournamentMatch`;
const USE_LIST_INTRA_SQUAD_TOURNAMENT_MATCHES_BY_TEAM_ID = `useListIntraSquadTournamentMatchesByTeamId`;
const USE_LIST_INTRA_SQUAD_TOURNAMENT_MATCHES_BY_PLAYER_ID = `useListIntraSquadTournamentMatchesByPlayerId`;
const USE_LIST_INTRA_SQUAD_TOURNAMENTS_BY_TEAM_ID = `useListIntraSquadTournamentsByTeamId`;
const USE_LIST_INTRA_SQUAD_TOURNAMENTS_BY_PLAYER_ID = `useListIntraSquadTournamentsByPlayerId`;
export const useCreateIntraSquadTournament = ({ team, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!team) {
                throw new Error("Team is not defined!");
            }
            return api.post(`/teams/${team.id}/create-intra-squad-tournament/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            if (!team)
                return;
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_FEED, team.id]);
            onSuccess(data);
        },
        onError,
    });
};
export const useRetrieveIntraSquadTournament = ({ id, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_INTRA_SQUAD_TOURNAMENT, id],
        queryFn: async () => {
            return (await api.get(`/intra-squad-tournaments/${id}/`, {})).data;
        },
        enabled: !!id && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useListIntraSquadTournamentMatches = ({ tournamentId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_INTRA_SQUAD_TOURNAMENT_MATCHES, tournamentId],
        queryFn: async () => {
            if (!tournamentId) {
                throw new Error("Tournament ID is not defined!");
            }
            const response = await api.get(`/intra-squad-tournaments/${tournamentId}/matches/`);
            return response.data;
        },
        enabled: !!tournamentId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useListIntraSquadTournamentTeams = ({ tournamentId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_INTRA_SQUAD_TOURNAMENT_TEAMS, tournamentId],
        queryFn: async () => {
            if (!tournamentId) {
                throw new Error("Tournament ID is not defined!");
            }
            const response = await api.get(`/intra-squad-tournaments/${tournamentId}/teams/`);
            return response.data;
        },
        enabled: !!tournamentId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveIntraSquadTournamentMatch = ({ id, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_INTRA_SQUAD_TOURNAMENT_MATCH, id],
        queryFn: async () => {
            return (await api.get(`/intra-squad-tournament-matches/${id}/`, {}))
                .data;
        },
        enabled: !!id && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useSubmitIntraSquadTournamentMatchStats = (intraSquadTournamentMatch) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!intraSquadTournamentMatch?.id) {
                throw new Error("Intra-squad tournament match must be defined!");
            }
            return api.post(`/intra-squad-tournament-matches/${intraSquadTournamentMatch.id}/submit-stats/`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([
                USE_LIST_INTRA_SQUAD_TOURNAMENT_MATCHES,
                intraSquadTournamentMatch.tournament,
            ]);
            queryClient.invalidateQueries([
                USE_LIST_INTRA_SQUAD_TOURNAMENT_MATCHES_BY_TEAM_ID,
                intraSquadTournamentMatch.team,
            ]);
            [
                ...intraSquadTournamentMatch.teamA.players,
                ...intraSquadTournamentMatch.teamB.players,
            ].forEach((player) => {
                queryClient.invalidateQueries([
                    USE_LIST_INTRA_SQUAD_TOURNAMENT_MATCHES_BY_PLAYER_ID,
                    player.id,
                ]);
            });
            queryClient.invalidateQueries([
                USE_RETRIEVE_INTRA_SQUAD_TOURNAMENT_MATCH,
                intraSquadTournamentMatch.id,
            ]);
        },
    });
};
export const useDeleteIntraSquadTournament = ({ intraSquadTournament, team, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            if (!intraSquadTournament) {
                throw new Error("IntraSquadMatch is undefined");
            }
            return api.delete(`/intra-squad-tournaments/${intraSquadTournament.id}/`);
        },
        onMutate,
        onSuccess: (data) => {
            if (!intraSquadTournament || !team)
                return;
            queryClient.invalidateQueries([
                USE_LIST_INTRA_SQUAD_TOURNAMENTS_BY_TEAM_ID,
                intraSquadTournament.team,
            ]);
            team?.players?.forEach((playerId) => {
                queryClient.invalidateQueries([
                    USE_LIST_INTRA_SQUAD_TOURNAMENTS_BY_PLAYER_ID,
                    playerId,
                ]);
            });
            queryClient.removeQueries([
                USE_RETRIEVE_INTRA_SQUAD_TOURNAMENT,
                intraSquadTournament.id,
            ]);
            queryClient.invalidateQueries([
                USE_RETRIEVE_TEAM_FEED,
                intraSquadTournament.team,
            ]);
            onSuccess(data);
        },
        onError,
    });
};
